import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import { IDialogFooterActionButton } from '../../../../../../../BaseComponents/Dialog/DialogFooter/EDDialogFooter';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import {
  FormUpdateAssociatonType,
  getFormUpdateValue,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import {
  TEDEKBokforderMeddelandeAVSANDAR_TYPEnum,
  TEDEKBokforderMinBokforderStatusEnum,
  TEDEKBokforderSTATUSEnum,
  TEDEKBokforderTYPEnum,
} from '../../../../../../../globalQueryTypes';
import { mergeBusinessDataFormValues } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { EDTextEdit } from '../../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDKeybind from '../../../../../../../BaseComponents/Keybinds/EDKeybind';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { NEWLY_ADDED_ID_PREFIX_FOR_UPDATE } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Const/BusinessDataFormConst';
import EDDivider from '../../../../../../../BaseComponents/Divider/EDDivider';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { StandardRutinContext } from '../../../../../_StandardRutin/StandardRutinContext';
import { BokforderType__Associations_Rader } from '../../Queries/_QueryTypes/BokforderType';
import { UtanordningType__Associations_Rader } from '../../Queries/_QueryTypes/UtanordningType';
import {
  certifyOrRetakeAllRows,
  findFranvaroForUser,
} from '../../Utils/BokforderUtils';
import { FranvaroContext } from '../../../../../SystemAdministration/ObjektVyer/User/Context/FranvaroContext/FranvaroContext';
import { EDAutoCompleteEdit } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import BokforderContext from '../BokforderContext/BokforderContext';

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  type: TEDEKBokforderTYPEnum;
}

interface ISendBackUserOption {
  id: string;
  label: string;
  value: string;
}

type TSendBackRow =
  | BokforderType__Associations_Rader
  | UtanordningType__Associations_Rader;

const BokforderSendBackDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { setOpen } = props;
  const formContext = useContext(BusinessDataFormContext);
  const saveForm = formContext?.saveForm;
  const { userID } = useContext(ActiveDatabaseContext);
  const standardRutinContext = useContext(StandardRutinContext);
  const franvaroContext = useContext(FranvaroContext);
  const bokforderContext = useContext(BokforderContext);
  const [sendBackMessage, setSendBackMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [sendbackUser, setSendBackUser] = useState<ISendBackUserOption | null>(
    null
  );
  const sendBackMessageInputRef = useRef<HTMLInputElement>(undefined);

  const sendbackTypeName =
    props.type === TEDEKBokforderTYPEnum.BOKFORINGSORDER
      ? 'bokföringsordern'
      : 'utanordningen';
  const trimmedSendBackMessage = sendBackMessage.trim();

  const availableAttestanter =
    props.type === TEDEKBokforderTYPEnum.BOKFORINGSORDER
      ? bokforderContext.allBokforderUsersWithKodbeh
      : bokforderContext.allUtanordningUsersWithKodbeh;

  const bestallareOption = useMemo(() => {
    const bestallare =
      formContext?.formState.currentValues?._Associations?.Bestallare?.Fullname;

    return {
      id: 'b',
      label: bestallare ? `Beställare: ${bestallare}` : 'Beställare',
      value: 'b',
    };
  }, [
    formContext?.formState.currentValues?._Associations?.Bestallare?.Fullname,
  ]);

  const rows = useMemo(
    () =>
      (formContext?.formState.currentValues?._Associations?.Rader ??
        []) as TSendBackRow[],
    [formContext?.formState.currentValues?._Associations?.Rader]
  );

  const attestantOptions = useMemo(
    () =>
      availableAttestanter
        .filter(attestant => {
          return rows.some(
            row =>
              row._Associations?.Attestant?.id === attestant.userID &&
              row.ATTESTERAD_DATUM != null
          );
        })
        .map(attestant => ({
          id: attestant.userID,
          label: `Attestant: ${attestant.namn}`,
          value: attestant.userID,
        })),
    [availableAttestanter, rows]
  );

  const sendBackOptions = useMemo(
    () => [
      bestallareOption,
      ...attestantOptions,
      ...(attestantOptions.length > 1
        ? [{ id: 'aa', value: 'aa', label: 'Alla attestanter' }]
        : []),
    ],
    [attestantOptions, bestallareOption]
  );

  const resetDialogState = useCallback(() => {
    setSendBackMessage('');
    setSendBackUser(null);
    setLoading(false);
  }, []);

  const onCancel = useCallback(() => {
    if (loading) {
      return;
    }

    setOpen(false);
  }, [loading, setOpen]);

  const onClick = useCallback(() => {
    if (
      loading ||
      sendbackUser == null ||
      trimmedSendBackMessage === '' ||
      !saveForm
    ) {
      return;
    }

    setLoading(true);
    const newValues: FormUpdateAssociatonType[][] = [];
    const todayDate = new Date();

    const messageValues: FormUpdateAssociatonType = {
      associationName: 'Meddelande',
      operation: 'Add_Values',
      id: NEWLY_ADDED_ID_PREFIX_FOR_UPDATE + 'BokforderMessage',
      values: {
        id: NEWLY_ADDED_ID_PREFIX_FOR_UPDATE + 'BokforderMessage',
        MEDDELANDE: trimmedSendBackMessage,
        AVSANDAR_TYP: TEDEKBokforderMeddelandeAVSANDAR_TYPEnum.FRAN_ATTESTANT,
      },
    };

    const messageRef: FormUpdateAssociatonType = {
      associationName: 'Attestant',
      operation: 'Ref',
      id: userID ?? '',
    };

    newValues.push(
      [
        {
          operation: 'OwnFields',
          values: {
            STATUS:
              sendbackUser.id === 'b'
                ? TEDEKBokforderSTATUSEnum.EJ_GODKAND
                : TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST,
            STATUS_DATUM: todayDate.toLocaleDateString('sv-SE'),
          },
        },
      ],
      [messageValues, messageRef]
    );

    const isUtanordning = props.type === TEDEKBokforderTYPEnum.UTANORDNING;
    const isKontrollattestant =
      isUtanordning &&
      formContext?.formState.currentValues?._Associations?.Kontrollattestant
        ?.id === userID;

    const kontrollattestantWithCurrentUserAsReserv = isUtanordning
      ? franvaroContext.franvaroReservForData.find(franvaro =>
          findFranvaroForUser(
            formContext?.formState.currentValues?._Associations
              ?.Kontrollattestant?.id,
            TEDEKBokforderTYPEnum.UTANORDNING,
            todayDate,
            franvaro
          )
        )
      : undefined;

    const canRetakeAllRowsForBestallare =
      (isKontrollattestant ||
        kontrollattestantWithCurrentUserAsReserv != undefined) &&
      formContext?.formState.currentValues?.MinBokforderStatus ===
        TEDEKBokforderMinBokforderStatusEnum.ATT_KONTROLLATTESTERA;

    const isSendbackToBestallare = sendbackUser.id === 'b';
    const isSendbackToAllaAttestanter = sendbackUser.id === 'aa';
    const isSendbackToSpecificAttestant =
      !isSendbackToAllaAttestanter &&
      !isSendbackToBestallare &&
      sendbackUser != null;
    const shouldRetakeAllRows =
      isSendbackToAllaAttestanter ||
      (isSendbackToBestallare && canRetakeAllRowsForBestallare);

    const rowsToRetake = shouldRetakeAllRows
      ? rows
      : isSendbackToSpecificAttestant
      ? rows.filter(row => row._Associations?.Attestant?.id === sendbackUser.id)
      : [];

    if (rowsToRetake.length > 0) {
      newValues.push(
        ...certifyOrRetakeAllRows(
          rowsToRetake as BokforderType__Associations_Rader[],
          ''
        )
      );
    }

    const mergedValues = mergeBusinessDataFormValues(
      getFormUpdateValue(newValues, formContext?.data ?? {}),
      formContext?.formState.values ?? {}
    );

    saveForm(
      mergedValues,
      () => {
        setLoading(false);
        setOpen(false);
        standardRutinContext.selectNextDetail.current?.(true);
      },
      () => {
        setLoading(false);
      }
    );
  }, [
    loading,
    sendbackUser,
    trimmedSendBackMessage,
    userID,
    formContext?.formState.currentValues?._Associations?.Kontrollattestant?.id,
    formContext?.formState.currentValues?.MinBokforderStatus,
    formContext?.formState.values,
    formContext?.data,
    franvaroContext.franvaroReservForData,
    props.type,
    rows,
    saveForm,
    setOpen,
    standardRutinContext.selectNextDetail,
  ]);

  const onCtrlEnterKeyDown = useCallback(() => {
    if (
      sendBackMessageInputRef.current &&
      document.activeElement === sendBackMessageInputRef.current
    ) {
      onClick();
    }
  }, [sendBackMessageInputRef, onClick]);

  const onEnterKeyDown = useCallback(() => {
    if (
      sendBackMessageInputRef.current &&
      document.activeElement !== sendBackMessageInputRef.current
    ) {
      onClick();
    }
  }, [sendBackMessageInputRef, onClick]);

  const footerActions: IDialogFooterActionButton[] = [
    {
      label: 'Avbryt',
      onClick: onCancel,
      buttonProps: {
        disabled: loading,
      },
    },
    {
      label: 'Skicka tillbaka',
      onClick: onClick,
      buttonProps: {
        disabled:
          loading || trimmedSendBackMessage === '' || sendbackUser === null,
        disabledReason: loading
          ? `Skickar tillbaka ${sendbackTypeName}`
          : sendbackUser === null
          ? 'Du måste välja mottagare'
          : trimmedSendBackMessage === ''
          ? 'Du måste skriva ett meddelande'
          : '',
      },
    },
  ];

  return (
    <EDKeybind keymap="Enter" onKeyDown={onEnterKeyDown}>
      <EDKeybind keymap="Enter" ctrl onKeyDown={onCtrlEnterKeyDown}>
        <EDDialog
          open={props.open}
          onClose={() => {
            if (!loading) {
              onCancel();
            }
          }}
          onExited={resetDialogState}
          maxWidth="sm"
          footerActions={footerActions}
          title={`Skicka tillbaka ${sendbackTypeName}`}
        >
          <EDAutoCompleteEdit
            options={sendBackOptions}
            getOptionLabel={option => option.label}
            value={sendbackUser}
            onChange={setSendBackUser}
            fullWidth
            label="Skicka tillbaka till"
            required
            disabled={loading}
            testId="skicka-tillbaka-bokforder-autocomplete"
          />
          <EDTypography mt={1}>
            {`Meddela varför ${sendbackTypeName} inte godkänns och returneras`}
          </EDTypography>
          <EDDivider marginTop={1} />
          {loading && <EDAvenyProgress />}
          <EDTextEdit
            label="Meddelande"
            required
            multiline
            disabled={loading}
            value={sendBackMessage}
            onChange={setSendBackMessage}
            inputRef={sendBackMessageInputRef}
            testId="skicka-tillbaka-bokforder-input"
          />
        </EDDialog>
      </EDKeybind>
    </EDKeybind>
  );
};

export default BokforderSendBackDialog;
